import { NavLink } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrgApps } from "@/hooks/useOrgApps";
import { signOut } from "@/lib/auth";
import { cn } from "@/lib/utils";
import bigLogo from "@/assets/big-logo.png";
import { isPlatformRole, type OrgUserRole } from "@/types/firestore";
import { VERSION_DISPLAY } from "@/lib/version";

interface NavItem {
  label: string;
  to: string;
  /** When set, renders a section header above this item */
  section?: string;
}

const platformNav: NavItem[] = [
  { label: "Dashboard", to: "/admin" },
  { label: "Organizations", to: "/admin/orgs" },
  { label: "Studios", to: "/admin/studios" },
  { label: "Apps", to: "/admin/apps" },
  { label: "Resources", to: "/admin/resources" },
  { label: "Communications", to: "/admin/communications" },
  { label: "Content", to: "/admin/content" },
  { label: "Contacts", to: "/admin/contacts" },
  { label: "Workshops", to: "/admin/workshops" },
  { label: "Platform Team", to: "/admin/team" },
  { label: "Settings", to: "/admin/settings" },
  { label: "Pickaxe Drift", to: "/admin/pickaxe-drift", section: "Observability" },
  { label: "MCP Approvals", to: "/admin/mcp/send-authorizations", section: "Communications" },
  { label: "Support Tickets", to: "/admin/support/tickets", section: "BiG-Support" },
  { label: "Support Agreements", to: "/admin/support/agreements" },
  { label: "Rate Cards", to: "/admin/support/rate-cards" },
  { label: "Month-end Close", to: "/admin/support/close" },
  { label: "Support Settings", to: "/admin/support/settings" },
];

// Org nav is split so the feature-gated "Apps" entry keeps its position
// between Studios and Resources.
const orgNavTop: NavItem[] = [
  { label: "Dashboard", to: "/org" },
  { label: "Studios", to: "/org/studios" },
];
const orgNavBottom: NavItem[] = [
  { label: "Resources", to: "/org/resources" },
  { label: "Users", to: "/org/users" },
  { label: "Team", to: "/org/team" },
  { label: "Settings", to: "/org/settings" },
];

const roleLabels: Record<OrgUserRole, string> = {
  PLATFORM_OWNER: "Platform Owner",
  PLATFORM_ADMIN: "Platform Admin",
  ORG_OWNER: "Organization Owner",
  ORG_ADMIN: "Organization Admin",
  ORG_REQUESTER: "Support Requester",
};

export function Sidebar() {
  const { orgUser, role, isImpersonating } = useAuth();
  const isPortal = !role || !isPlatformRole(role);
  const orgId = isPortal ? orgUser?.orgId ?? undefined : undefined;

  // Resolves the org doc (for the header) + its entitled apps. Idle for
  // platform users (static nav).
  const { org, apps } = useOrgApps(orgId, isPortal && role !== "ORG_REQUESTER");

  const workshopsEnabled = org?.features?.workshops?.enabled === true;

  let navItems: NavItem[];
  if (isPortal) {
    // A single "Apps" entry that opens the org's app hub — shown only when the
    // org actually has at least one app enabled.
    const appItems: NavItem[] =
      apps.length > 0 ? [{ label: "Apps", to: "/org/apps" }] : [];
    const workshopItems: NavItem[] = workshopsEnabled
      ? [{ label: "Workshops", to: "/org/workshops" }]
      : [];
    const supportItems = org?.features?.support?.enabled === true
      ? [{ label: "Support", to: "/org/support" }] : [];
    navItems = role === "ORG_REQUESTER" ? [{ label: "Support", to: "/org/support" }]
      : [...orgNavTop, ...appItems, ...supportItems, ...workshopItems, ...orgNavBottom];
  } else {
    navItems = platformNav;
  }

  return (
    <aside className="flex h-full w-60 flex-col border-r border-border bg-sidebar">
      <div className="border-b border-border px-4 py-4">
        {isPortal && org?.logo ? (
          <img src={org.logo} alt={org.name} className="mb-2 h-8 max-w-[160px] object-contain" />
        ) : (
          <img src={bigLogo} alt="BiG" className="mb-2 h-8" />
        )}
        <h1 className="font-heading text-lg font-bold text-text">
          {isPortal && org?.name ? org.name : "Studio Manager"}
        </h1>
        <p className="font-body text-xs text-text-muted">
          {isImpersonating
            ? "Viewing as Org"
            : role
              ? roleLabels[role]
              : ""}
        </p>
      </div>

      <nav className="flex-1 space-y-0.5 px-2 py-3">
        {navItems.map((item) => (
          <div key={item.to}>
            {item.section && (
              <div className="mb-1 mt-4 px-3 font-heading text-[10px] font-semibold uppercase tracking-wider text-text-muted/60">
                {item.section}
              </div>
            )}
            <NavLink
              to={item.to}
              end={item.to === "/admin" || item.to === "/org"}
              className={({ isActive }) =>
                cn(
                  "block rounded-md px-3 py-2 font-heading text-sm font-semibold transition-colors",
                  isActive
                    ? "border-l-2 border-teal bg-white text-teal"
                    : "text-text-muted hover:bg-white hover:text-text"
                )
              }
            >
              {item.label}
            </NavLink>
          </div>
        ))}
      </nav>

      <div className="border-t border-border px-4 py-3">
        <div className="mb-2 truncate font-body text-sm text-text-muted">
          {orgUser?.email}
        </div>
        <button
          onClick={() => signOut()}
          className="w-full rounded-md px-3 py-1.5 text-left font-body text-sm text-text-muted hover:bg-white hover:text-text transition-colors"
        >
          Sign out
        </button>
        <div className="mt-2 font-body text-[10px] text-text-muted/50">
          {VERSION_DISPLAY}
        </div>
      </div>
    </aside>
  );
}
